import { computed } from 'vue';
import type { Ref } from 'vue';
import { firstEntry } from '~/utils/firstEntry';
import { resolveContentfulPageRouteMode } from '~/utils/contentful/resolveContentfulPageRouteMode';
import {
  useContentfulPage,
  type UseContentfulPageResponseModel
} from '~/composables/useContentfulPage';
import { convertWebappToContentfulLocaleCode } from '~/utils/convertWebappToContentfulLocaleCode';

/**
 * Resolves the same `/api/contentful` payload as the active CMS page route, so
 * consumers (layout header, analytics) reuse the existing `useFetch` async data
 * instead of persisting a second copy in `useState`.
 *
 * `when` is false on non-CMS routes (e.g. PDP) so no Contentful request is made.
 *
 * Uses `router.currentRoute` (updated by Vue Router in `finalizeNavigation`, before
 * `router.afterEach`) instead of `nuxtApp._route` (updated in `afterEach`) so that
 * the watcher and the initial `syncFetch` call fire with the correct route on client
 * navigation — avoiding the race where the page render (M1 flush) sees stale route
 * values and resolves `whenResolved` as `false`.
 */
export function useContentfulPageFromRoute(): Promise<
  Ref<UseContentfulPageResponseModel | null>
> {
  const router = useRouter();

  const routeKind = computed(() =>
    resolveContentfulPageRouteMode(router.currentRoute.value)
  );
  const when = computed(() => routeKind.value !== false);

  const idArg = computed(() =>
    routeKind.value === 'id'
      ? (firstEntry(router.currentRoute.value.params.id) ?? '')
      : undefined
  );
  const slugArg = computed(() =>
    routeKind.value === 'slug'
      ? ensureArray(router.currentRoute.value.params.slug)
          .filter(Boolean)
          .join('/')
      : undefined
  );
  const locale = computed(() => {
    const raw =
      firstEntry(router.currentRoute.value.params.locale)?.toLowerCase() ?? '';
    return convertWebappToContentfulLocaleCode(raw);
  });

  return useContentfulPage({
    id: idArg,
    slug: slugArg,
    locale,
    when
  });
}
